import { Injectable } from "@angular/core";
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from "@angular/router";
import { ErrorsService } from "common";
import { OidcService } from "../oidc.service";

@Injectable({
  providedIn: "root",
})
export class CallbackGuard implements CanActivate {
  constructor(
    private oidcService: OidcService,
    private router: Router,
    private errorsService: ErrorsService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    this.oidcService.handleCallback().subscribe((res) => {
      if (res) {
        this.handleRedirect();
        return;
      }

      this.errorsService.showGeneralErrorPage();
    });

    return false;
  }

  private handleRedirect(): void {
    const historyQuery = sessionStorage.getItem("navigation.historyQuery");
    const history = sessionStorage.getItem("navigation.history");
    const navigateTo = this.getRedirectTarget(historyQuery, history);

    sessionStorage.removeItem("navigation.historyQuery");
    sessionStorage.removeItem("navigation.history");
    this.router.navigateByUrl(navigateTo, { replaceUrl: true });
  }

  // A root-path entry is not a usable destination, but its query string is still the landing
  // link's (e.g. a marketing "?campaign=" link the dashboard stores for the draw request). Carry
  // it onto "home" — "home" → dashboard keeps query params — so it survives sign-in. It ranks
  // above the path-only "navigation.history", which never carries the query.
  private getRedirectTarget(historyQuery: string | null, history: string | null): string {
    if (this.isUsableTarget(historyQuery)) {
      return historyQuery;
    }
    const root = historyQuery ? this.splitTarget(historyQuery) : null;
    if (root?.path === "" && root.query) {
      return `home?${root.query}`;
    }
    if (this.isUsableTarget(history)) {
      return history;
    }
    return "home";
  }

  // A stored history entry is only a usable post-login destination when it points at a real
  // page. The root path ("/", "/?") and the auth-flow routes just bounce back through the
  // sign-in redirect (and, while authenticated, the navigate-to-dashboard is dropped because it
  // fires during this navigation's cancellation), leaving the app stuck on a blank "/". Fall
  // back to "home" (→ dashboard) for those.
  private isUsableTarget(target: string | null): boolean {
    if (!target) {
      return false;
    }
    const { path } = this.splitTarget(target);
    if (path === "") {
      return false;
    }
    const excludedPaths = ["sign-in", "login", "callback", "sign-out", "error"];
    return !excludedPaths.some((excluded) => path.includes(excluded));
  }

  // Splits a stored entry into its path (trailing slashes trimmed, so the root is "") and its
  // query string (without the "?"); any fragment is dropped.
  private splitTarget(target: string): { path: string; query: string } {
    const [pathAndQuery] = target.split("#");
    const queryIndex = pathAndQuery.indexOf("?");
    const rawPath = queryIndex < 0 ? pathAndQuery : pathAndQuery.substring(0, queryIndex);
    return {
      path: rawPath.replace(/\/+$/, ""),
      query: queryIndex < 0 ? "" : pathAndQuery.substring(queryIndex + 1),
    };
  }
}
